import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, bindText, html } from "../lib/bind";
import { daysSince, formatDateTime } from "../lib/date";
import { currentScope } from "../lib/scope";
import {
  type BackupStatus,
  backupDownloadHref,
  companyExportHref,
  getBackupJob,
  getBackupStatus,
  startBackup,
} from "../services/backups";
import { breadcrumb } from "../stores/breadcrumb";
import { session } from "../stores/session";
import { toast } from "../lib/toast";

const POLL_MS = 2000;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

export function backupsView(): Node {
  const status = signal<BackupStatus | null>(null);
  const loadError = signal("");
  const running = signal(false);
  const jobMessage = signal("");

  breadcrumb.value = { label: "Settings", path: "/settings" };
  const scope = currentScope();
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  scope.cleanup(() => {
    breadcrumb.value = null;
    if (pollTimer !== null) clearTimeout(pollTimer);
  });

  function reload(): Promise<void> {
    return getBackupStatus()
      .then((result) => {
        status.value = result;
        // A run started before this page was opened (or before a reload) is still ours to
        // follow — otherwise the button would offer a second one and get a 409.
        if (result.running_job_id && !running.value) {
          running.value = true;
          jobMessage.value = "Backup in progress…";
          poll(result.running_job_id);
        }
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }

  function poll(jobId: string): void {
    pollTimer = setTimeout(() => {
      getBackupJob(jobId)
        .then((job) => {
          if (job.status === "done") {
            running.value = false;
            jobMessage.value = "";
            toast.success("Backup complete.");
            void reload();
          } else if (job.status === "failed") {
            running.value = false;
            jobMessage.value = "";
            // Surfaced in full rather than as "backup failed": the useful cases are a
            // pg_dump/server version mismatch and a full disk, and both name themselves.
            loadError.value = job.error ?? "Backup failed";
          } else {
            poll(jobId);
          }
        })
        .catch((err: unknown) => {
          running.value = false;
          jobMessage.value = "";
          loadError.value =
            err instanceof ApiRequestError ? err.body.message : "Lost track of the backup job";
        });
    }, POLL_MS);
  }

  function runNow(): void {
    loadError.value = "";
    running.value = true;
    jobMessage.value = "Backup in progress…";
    startBackup()
      .then((job) => {
        poll(job.id);
      })
      .catch((err: unknown) => {
        running.value = false;
        jobMessage.value = "";
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to start";
      });
  }

  const latestText = computed(() => {
    const latest = status.value?.latest;
    if (!status.value) return "—";
    if (!latest) return "No backup has ever completed on this instance.";
    // Only from a whole day out: a backup taken minutes ago reading "0 days ago" is worse
    // than saying nothing, and the timestamp is right there.
    const days = daysSince(latest.created_at);
    const age = days !== null && days >= 1 ? ` · ${String(days)} days ago` : "";
    return `${formatDateTime(latest.created_at)} · ${formatSize(latest.size_bytes)}${age}`;
  });
  const staleWarning = computed(() => {
    const value = status.value;
    if (!value?.stale) return "";
    return `No verified backup in the last ${String(value.stale_after_hours)} hours.`;
  });
  const showStale = computed(() => staleWarning.value !== "");
  const offsiteText = computed(() =>
    status.value?.offsite === "not_configured"
      ? "Offsite copy: not configured yet."
      : `Offsite copy: ${status.value?.offsite ?? "—"}`,
  );
  const hasArchive = computed(() => status.value?.latest != null);
  const restoreCommand = computed(() => status.value?.restore_command ?? "");

  const fragment = html`<div class="view">
    <div class="card">
      <div class="card-label">Last verified backup</div>
      <p class="backup-latest"></p>
      <div class="banner banner-warning backup-stale" role="status"></div>
      <p class="field-hint backup-offsite"></p>
      <p class="field-hint">
        Backups run nightly from a systemd timer on the deploy host
        (<code>owlishbean-backup.timer</code>). The app cannot see the host's scheduler, so
        it reports what is actually on disk rather than a predicted next run.
      </p>
      <div class="form-actions">
        <button type="button" class="btn btn-primary backup-now">Back up now</button>
        <a class="btn backup-download" download>Download latest</a>
        <a class="btn backup-export" download>Export this company (JSON)</a>
      </div>
      <p class="field-hint backup-job-message"></p>
      <p class="form-error backup-error"></p>
    </div>

    <div class="card">
      <div class="card-label">Restoring</div>
      <p>
        Restore stays on the command line by design — a web button inside the thing being
        replaced is the wrong shape. Download the archive, then run this on the deploy host:
      </p>
      <pre class="restore-command"></pre>
      <p class="field-hint">
        Add <code>--force</code> if the target database is not empty; it snapshots the
        current one to <code>pre-restore-&lt;timestamp&gt;.dump</code> first.
      </p>
    </div>
  </div>`;

  const latestEl = fragment.querySelector<HTMLElement>(".backup-latest");
  const staleEl = fragment.querySelector<HTMLElement>(".backup-stale");
  const offsiteEl = fragment.querySelector<HTMLElement>(".backup-offsite");
  const runBtn = fragment.querySelector<HTMLButtonElement>(".backup-now");
  const downloadLink = fragment.querySelector<HTMLAnchorElement>(".backup-download");
  const jobMessageEl = fragment.querySelector<HTMLElement>(".backup-job-message");
  const errorEl = fragment.querySelector<HTMLParagraphElement>(".backup-error");
  const restoreEl = fragment.querySelector<HTMLElement>(".restore-command");
  const exportLink = fragment.querySelector<HTMLAnchorElement>(".backup-export");

  if (
    !latestEl ||
    !staleEl ||
    !offsiteEl ||
    !runBtn ||
    !downloadLink ||
    !jobMessageEl ||
    !errorEl ||
    !restoreEl ||
    !exportLink
  ) {
    throw new Error("backups markup missing expected elements");
  }

  bindText(latestEl, latestText);
  bindText(staleEl, staleWarning);
  bindShow(staleEl, showStale);
  bindText(offsiteEl, offsiteText);
  bindText(jobMessageEl, jobMessage);
  bindErrorText(errorEl, loadError);
  bindText(restoreEl, restoreCommand);
  bindShow(downloadLink, hasArchive);

  scope.effect(() => {
    runBtn.disabled = running.value;
    runBtn.textContent = running.value ? "Backing up…" : "Back up now";
  });
  scope.effect(() => {
    const latest = status.value?.latest;
    if (latest) downloadLink.href = backupDownloadHref(latest.filename);
  });
  scope.effect(() => {
    const companyId = session.companyId.value;
    if (companyId) exportLink.href = companyExportHref(companyId);
  });

  runBtn.addEventListener("click", runNow);
  void reload();

  return fragment;
}
